/**
 * Import Instagram Posts - Side Sheet
 *
 * Modern side sheet implementation replacing the modal dialog.
 * Uses shadcn Sheet component with:
 * - 544px fixed width
 * - Slide from right animation
 * - Fixed header and footer
 * - Scrollable content area
 * - Two tab views: Feed post and Other/Reels
 */

import React, { useEffect, useMemo, useState, useRef } from "react";
import { useSelector } from "react-redux";
import { cn } from "../../lib/utils";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetFooter,
} from "../ui/sheet";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { Button } from "../ui/button";
import {
  AccountDropdown,
  accountTypesToSelectorAccounts,
  accountTypesToSelectorGroups,
} from "../ui/selectors";
import FeedPostsImportModern, {
  FeedPostsImportHandle,
} from "./FeedPostsImportModern";
import IgtvReelsImportModern, {
  IgtvReelsImportHandle,
} from "./IgtvReelsImportModern";
import { getAccounts } from "../../selectors/postsPageSelectors";
import { AccountType } from "../../shared/types/accounts";
import { useInstagramApiPublishingOn } from "../../utils/feature-toggle-utils";

interface ImportIgPostsSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onImported?: () => void;
  accountId?: string;
}

const ImportIgPostsSheet: React.FC<ImportIgPostsSheetProps> = ({
  open,
  onOpenChange,
  onImported,
  accountId,
}) => {
  const [activeTab, setActiveTab] = useState<"feed" | "reels">("feed");
  const accounts = useSelector(getAccounts);
  const [selectedAccount, setSelectedAccount] = useState<
    AccountType | undefined
  >();
  const [feedSelectionCount, setFeedSelectionCount] = useState(0);

  const feedRef = useRef<FeedPostsImportHandle>(null);
  const reelsRef = useRef<IgtvReelsImportHandle>(null);
  const [reelsCanImport, setReelsCanImport] = useState(false);
  const instagramAccounts = useMemo(
    () =>
      accounts.filter(
        (account: AccountType) =>
          account.platformType === "IG" || account.platformName === "instagram"
      ),
    [accounts]
  );
  const instagramApiPublishing = useInstagramApiPublishingOn();
  const selectorAccounts = useMemo(
    () =>
      accountTypesToSelectorAccounts(instagramAccounts, {
        instagramApiPublishing,
      }),
    [instagramAccounts, instagramApiPublishing]
  );
  const selectorGroups = useMemo(
    () =>
      accountTypesToSelectorGroups(instagramAccounts, {
        instagramApiPublishing,
      }),
    [instagramAccounts, instagramApiPublishing]
  );

  useEffect(() => {
    if (accountId && accounts) {
      setSelectedAccount(
        accounts.find((account: AccountType) => account._id === accountId)
      );
    }
  }, [accounts, accountId]);

  const handleClose = () => {
    onOpenChange(false);
  };

  const handleImport = async () => {
    if (activeTab === "feed" && feedRef.current) {
      const success = await feedRef.current.startImport();
      if (success) {
        handleClose();
      }
    } else if (activeTab === "reels" && reelsRef.current) {
      const success = await reelsRef.current.startImport();
      if (success) {
        handleClose();
      }
    }
  };

  const isImportDisabled = () => {
    if (activeTab === "feed") {
      return feedSelectionCount === 0 || feedRef.current?.isImporting();
    } else {
      return !reelsCanImport || reelsRef.current?.isImporting();
    }
  };

  const getImportButtonText = () => {
    const isImporting =
      (activeTab === "feed" && feedRef.current?.isImporting()) ||
      (activeTab === "reels" && reelsRef.current?.isImporting());

    if (isImporting) {
      return (
        <>
          <i className="fa-regular fa-spinner-third fa-spin mr-2" />
          Importing...
        </>
      );
    }

    if (activeTab === "feed") {
      if (feedSelectionCount > 0) {
        return `Import ${feedSelectionCount} ${
          feedSelectionCount === 1 ? "Post" : "Posts"
        }`;
      }
      return "Import Posts";
    } else {
      return "Import Post";
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className={cn(
          // Fixed 544px width for side sheet
          "w-[544px] max-w-[544px]",
          // Full height
          "h-full",
          // Flex column layout for fixed header/footer
          "flex flex-col",
          // Border and styling
          "border-l border-neutral-100 rounded-l-lg",
          // Remove default padding - we'll add it per section
          "p-0"
        )}
        showCloseButton={false}
      >
        {/* Header - Fixed at top */}
        <SheetHeader className="px-6 py-4 border-b border-neutral-100 flex-shrink-0">
          <div className="flex items-center justify-between">
            <SheetTitle className="font-gotham text-lg font-semibold text-neutral-900">
              Import Instagram Posts
            </SheetTitle>
            <button
              onClick={handleClose}
              className="rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
            >
              <i className="fa-solid fa-xmark h-4 w-4 text-neutral-900" />
              <span className="sr-only">Close</span>
            </button>
          </div>

          {/* Compact inline alert */}
          <div className="mt-3 bg-blue-50 text-blue-600 text-sm px-3 py-2 rounded-lg flex items-center gap-2">
            <i className="fa-regular fa-circle-info" />
            <span>
              Import posts to sync your planner with your feed. Won't be posted
              via Sked.
            </span>
          </div>
        </SheetHeader>

        {/* Content Area - Scrollable */}
        <div className="flex-1 overflow-y-auto px-6 pb-6">
          {/* Account Selector */}
          <div className="space-y-1.5 mb-4">
            <label className="font-gotham text-sm font-medium text-neutral-700">
              Account
            </label>
            <AccountDropdown
              accounts={selectorAccounts}
              groups={selectorGroups}
              value={
                selectedAccount
                  ? { kind: "account", id: selectedAccount._id }
                  : undefined
              }
              onChange={(choice) => {
                if (choice.kind !== "account") return;
                setSelectedAccount(
                  instagramAccounts.find((account) => account._id === choice.id)
                );
              }}
              searchable
              aria-label="Account"
            />
          </div>

          {/* Tabs for post type */}
          {selectedAccount && (
            <Tabs
              value={activeTab}
              onValueChange={(val) => setActiveTab(val as "feed" | "reels")}
              className="w-full"
            >
              <TabsList className="grid w-full grid-cols-2 h-10 rounded-lg p-0.5 mb-4">
                <TabsTrigger
                  value="feed"
                  className="h-full rounded-md font-inter text-sm font-medium text-neutral-500 data-[state=active]:bg-white data-[state=active]:text-neutral-900 data-[state=active]:shadow-sm"
                >
                  Feed post
                </TabsTrigger>
                <TabsTrigger
                  value="reels"
                  className="h-full rounded-md font-inter text-sm font-medium text-neutral-500 data-[state=active]:bg-white data-[state=active]:text-neutral-900 data-[state=active]:shadow-sm"
                >
                  Other/Reels
                </TabsTrigger>
              </TabsList>

              <TabsContent value="feed" className="mt-0">
                <FeedPostsImportModern
                  ref={feedRef}
                  accountId={selectedAccount._id}
                  onImported={onImported}
                  onSelectionChange={setFeedSelectionCount}
                />
              </TabsContent>

              <TabsContent value="reels" className="mt-0">
                <IgtvReelsImportModern
                  ref={reelsRef}
                  accountId={selectedAccount._id}
                  onImported={onImported}
                  onCanImportChange={setReelsCanImport}
                />
              </TabsContent>
            </Tabs>
          )}

          {/* Empty state when no account selected */}
          {!selectedAccount && (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <div className="w-16 h-16 rounded-full bg-neutral-100 flex items-center justify-center mb-4">
                <i className="fa-brands fa-instagram text-neutral-400 text-2xl" />
              </div>
              <h3 className="font-gotham text-base font-semibold text-neutral-900 mb-1">
                Select an account
              </h3>
              <p className="font-inter text-sm text-neutral-500">
                Choose an Instagram account to import posts
              </p>
            </div>
          )}
        </div>

        {/* Footer - Fixed at bottom */}
        <SheetFooter className="px-6 py-4 border-t border-neutral-100 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.05)] flex-shrink-0">
          {/* Selection count for Feed post tab */}
          {selectedAccount && activeTab === "feed" && (
            <p className="font-inter text-sm text-neutral-600 mb-3">
              {feedSelectionCount > 0
                ? `${feedSelectionCount} ${
                    feedSelectionCount === 1 ? "post" : "posts"
                  } selected`
                : "No posts selected"}
            </p>
          )}

          <div className="flex flex-col gap-3 w-full">
            <Button
              variant="blue-primary"
              disabled={!selectedAccount || isImportDisabled()}
              className="w-full"
              onClick={handleImport}
            >
              {getImportButtonText()}
            </Button>
            <Button
              variant="outline"
              onClick={handleClose}
              className="w-full border-neutral-200 hover:border-neutral-300"
            >
              Cancel
            </Button>
          </div>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
};

export default ImportIgPostsSheet;
